import { useEffect, useReducer, useRef, type ReactNode } from 'react';
import type { CropSpec } from '../utils/editRecipe';
import { useAuth } from './AuthContext';
import {
  DEFAULT_SIZE,
  DEFAULT_FINISH,
  DEFAULT_BORDER,
  type ScanMeta,
  type PrintLine,
  type BulkPatch,
  type LinePatch,
  type PrintSelectionContextValue,
  PrintSelectionContext,
} from './PrintSelectionContext';

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 99;

type Action =
  | { type: 'TOGGLE_SCAN'; meta: ScanMeta }
  | { type: 'ADD_LINE'; meta: ScanMeta; size: string; finish: string; border: boolean }
  | { type: 'REMOVE_LINE'; id: string }
  | { type: 'UPDATE_LINE'; id: string; patch: LinePatch }
  | { type: 'SELECT_ROLL'; metas: ScanMeta[] }
  | { type: 'DESELECT_SCANS'; scanIds: string[] }
  | { type: 'APPLY_TO_ROLL'; rollId: string; patch: BulkPatch }
  | { type: 'APPLY_TO_ALL'; patch: BulkPatch }
  | { type: 'CLEAR' }
  | { type: 'HYDRATE'; metas: ScanMeta[] }
  | { type: 'LOAD'; lines: PrintLine[] };

function newId(): string {
  return crypto.randomUUID();
}

function makeLine(
  meta: ScanMeta,
  size: string,
  finish: string,
  border: boolean = DEFAULT_BORDER,
): PrintLine {
  return {
    id: newId(),
    scan_id: meta.scan_id,
    filename: meta.filename,
    thumbnail_url: meta.thumbnail_url,
    roll_id: meta.roll_id,
    order_id: meta.order_id,
    size,
    finish,
    border,
    crop: null,
    quantity: 1,
  };
}

/**
 * Merge a bulk patch into a line. Every field is tested against `undefined`
 * rather than truthiness — `border: false` ("no border") is a real instruction
 * the customer chose, not an absent one.
 *
 * A bulk size change drops any crop back to the whole-frame letterbox. A crop
 * is shaped to one paper aspect and is meaningless against another, and this
 * reducer has no access to the image's own aspect (it lives with the loaded
 * thumbnail), so it cannot re-fit. The basket re-fits per line before
 * dispatching, so in practice a customer's framing is preserved — this is the
 * backstop that guarantees a crop can never outlive the aspect it was built
 * for.
 */
function applyBulkPatch(line: PrintLine, patch: BulkPatch): PrintLine {
  const sizeChanged = patch.size !== undefined && patch.size !== line.size;
  return {
    ...line,
    ...(patch.size !== undefined ? { size: patch.size } : {}),
    ...(patch.finish !== undefined ? { finish: patch.finish } : {}),
    ...(patch.border !== undefined ? { border: patch.border } : {}),
    ...(sizeChanged ? { crop: null } : {}),
  };
}

function clampQty(q: number): number {
  if (Number.isNaN(q)) return MIN_QUANTITY;
  return Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, Math.trunc(q)));
}

function reducer(state: PrintLine[], action: Action): PrintLine[] {
  switch (action.type) {
    case 'TOGGLE_SCAN': {
      const present = state.some((l) => l.scan_id === action.meta.scan_id);
      return present
        ? state.filter((l) => l.scan_id !== action.meta.scan_id)
        : [...state, makeLine(action.meta, DEFAULT_SIZE, DEFAULT_FINISH)];
    }
    case 'ADD_LINE':
      return [...state, makeLine(action.meta, action.size, action.finish, action.border)];
    case 'REMOVE_LINE':
      return state.filter((l) => l.id !== action.id);
    case 'UPDATE_LINE':
      return state.map((l) =>
        l.id === action.id
          ? {
              ...l,
              ...(action.patch.size !== undefined ? { size: action.patch.size } : {}),
              ...(action.patch.finish !== undefined ? { finish: action.patch.finish } : {}),
              ...(action.patch.border !== undefined ? { border: action.patch.border } : {}),
              // `null` is meaningful here — "back to the whole-frame letterbox".
              ...(action.patch.crop !== undefined ? { crop: action.patch.crop } : {}),
              ...(action.patch.quantity !== undefined
                ? { quantity: clampQty(action.patch.quantity) }
                : {}),
            }
          : l,
      );
    case 'SELECT_ROLL': {
      const present = new Set(state.map((l) => l.scan_id));
      const additions = action.metas
        .filter((m) => !present.has(m.scan_id))
        .map((m) => makeLine(m, DEFAULT_SIZE, DEFAULT_FINISH));
      return [...state, ...additions];
    }
    case 'DESELECT_SCANS': {
      const remove = new Set(action.scanIds);
      return state.filter((l) => !remove.has(l.scan_id));
    }
    case 'APPLY_TO_ROLL':
      return state.map((l) =>
        l.roll_id === action.rollId ? applyBulkPatch(l, action.patch) : l,
      );
    case 'APPLY_TO_ALL':
      return state.map((l) => applyBulkPatch(l, action.patch));
    case 'CLEAR':
      return [];
    case 'HYDRATE': {
      const byScan = new Map(action.metas.map((m) => [m.scan_id, m]));
      return state.map((l) => {
        const m = byScan.get(l.scan_id);
        return m
          ? {
              ...l,
              filename: m.filename,
              thumbnail_url: m.thumbnail_url,
              roll_id: m.roll_id,
              order_id: m.order_id,
            }
          : l;
      });
    }
    case 'LOAD':
      return action.lines;
    default:
      return state;
  }
}

/** Server persistence shape (mirrors the backend PrintSelectionItem). */
interface ServerSelectionItem {
  scan_id: string;
  size: string;
  finish: string;
  /** Absent on selections saved before the border option existed. */
  border?: boolean;
  /** Absent (or null) means the whole frame is letterboxed onto the paper. */
  crop?: CropSpec | null;
  quantity: number;
}

async function fetchSelection(): Promise<ServerSelectionItem[]> {
  const res = await fetch('/api/v1/print/selection', { credentials: 'include' });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data.items) ? data.items : [];
}

async function saveSelection(items: ServerSelectionItem[]): Promise<void> {
  await fetch('/api/v1/print/selection', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  }).catch(() => {
    // Best-effort persistence; the in-memory selection remains authoritative
    // for the session.
  });
}

export function PrintSelectionProvider({ children }: { children: ReactNode }) {
  const { authenticated, loading } = useAuth();
  const [lines, dispatch] = useReducer(reducer, []);
  const loadedRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the persisted selection once the customer is known to be signed in.
  //
  // This provider wraps the whole app, so without the guard it fired on every
  // page load — the login screen, and the admin pages, where the session is an
  // admin one and the customer authoriser rejects it. Every one of those loads
  // produced a failed request the page had no use for: 401 signed out, 403 as
  // an admin. Harmless in itself, but a permanent red herring in the network
  // tab, and this codebase has already lost an afternoon to a 403 that turned
  // out to be real.
  useEffect(() => {
    if (loading || !authenticated) return;
    let cancelled = false;
    fetchSelection().then((items) => {
      if (cancelled) return;
      const restored: PrintLine[] = items.map((it) => ({
        id: newId(),
        scan_id: it.scan_id,
        filename: it.scan_id, // placeholder until the gallery hydrates it
        thumbnail_url: '',
        size: it.size || DEFAULT_SIZE,
        finish: it.finish || DEFAULT_FINISH,
        border: it.border ?? DEFAULT_BORDER,
        crop: it.crop ?? null,
        quantity: clampQty(it.quantity),
      }));
      if (restored.length > 0) dispatch({ type: 'LOAD', lines: restored });
      loadedRef.current = true;
    });
    return () => {
      cancelled = true;
    };
  }, [authenticated, loading]);

  // Debounced save of the minimal selection whenever it changes (after load).
  //
  // `loadedRef` gates this, and it is only set once the load above has run —
  // which now requires a signed-in customer. So a signed-out or admin session
  // never PUTs either.
  useEffect(() => {
    if (!loadedRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveSelection(
        lines.map((l) => ({
          scan_id: l.scan_id,
          size: l.size,
          finish: l.finish,
          border: l.border,
          // Omit rather than send null: the backend models "no crop" as an
          // absent optional, and a letterbox is the absence of a crop.
          ...(l.crop ? { crop: l.crop } : {}),
          quantity: l.quantity,
        })),
      );
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [lines]);

  const value: PrintSelectionContextValue = {
    lines,
    totalQuantity: lines.reduce((sum, l) => sum + l.quantity, 0),
    isScanSelected: (scanId) => lines.some((l) => l.scan_id === scanId),
    toggleScan: (meta) => dispatch({ type: 'TOGGLE_SCAN', meta }),
    addLine: (meta, size = DEFAULT_SIZE, finish = DEFAULT_FINISH, border = DEFAULT_BORDER) =>
      dispatch({ type: 'ADD_LINE', meta, size, finish, border }),
    removeLine: (id) => dispatch({ type: 'REMOVE_LINE', id }),
    updateLine: (id, patch) => dispatch({ type: 'UPDATE_LINE', id, patch }),
    selectRoll: (metas) => dispatch({ type: 'SELECT_ROLL', metas }),
    deselectScans: (scanIds) => dispatch({ type: 'DESELECT_SCANS', scanIds }),
    applyToRoll: (rollId, patch) => dispatch({ type: 'APPLY_TO_ROLL', rollId, patch }),
    applyToAll: (patch) => dispatch({ type: 'APPLY_TO_ALL', patch }),
    clearSelection: () => dispatch({ type: 'CLEAR' }),
    hydrateFromScans: (metas) => dispatch({ type: 'HYDRATE', metas }),
  };

  return (
    <PrintSelectionContext.Provider value={value}>
      {children}
    </PrintSelectionContext.Provider>
  );
}
